import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { joinStudyGroup, leaveStudyGroup, deleteStudyGroup, fetchQuiz } from '../api';
import axios from 'axios';
import io from 'socket.io-client';
import '../App.css';

const socket = io("https://scalable-backend-n08a.onrender.com", {
  transports: ['websocket'],
  reconnection: true,
  reconnectionAttempts: 5,
  reconnectionDelay: 2000
});

const StudyGroupDetails = () => {
  const { groupId } = useParams();
  const [group, setGroup] = useState(null);
  const [quizQuestions, setQuizQuestions] = useState([]);
  const [showQuiz, setShowQuiz] = useState(false);
  const [answersState, setAnswersState] = useState({});
  const [quizSubmitted, setQuizSubmitted] = useState(false);
  const [score, setScore] = useState(0);
  const [groupScores, setGroupScores] = useState([]);
  const navigate = useNavigate();

  let currentUser = null;
  try {
    const storedUser = localStorage.getItem("user");
    currentUser = storedUser ? JSON.parse(storedUser) : null;
  } catch (err) {
    console.error("Failed to parse user from localStorage:", err);
  }

  const token = localStorage.getItem("token");
  const currentUserId = currentUser?.user_id || null;
  const currentUsername = currentUser?.email.split('@')[0] || null;

  const fetchGroup = useCallback(() => {
    axios.get(`${process.env.REACT_APP_API_URL}/study-groups`)
      .then(res => {
        const foundGroup = res.data.find(g => g._id === groupId);
        setGroup(foundGroup);
      })
      .catch(err => console.error(err));
  }, [groupId]);

  useEffect(() => {
    console.log("Socket connected:", socket.connected);
  }, []);

  useEffect(() => {
    fetchGroup();
  }, [fetchGroup]);

  useEffect(() => {
    if (!groupId) return;

    socket.emit('joinGroupRoom', { groupId });

    socket.on('quizStart', () => {
      handleFetchQuiz();
    });

    socket.on('startQuiz', (data) => {
      console.log("Received quiz from backend:", data.questions);
      setQuizQuestions(data.questions);
      setAnswersState({});
      setShowQuiz(true);
      setQuizSubmitted(false);
      setGroupScores([]);
    });

    socket.on('scoreUpdate', ({ username, score }) => {
      setGroupScores(prev => [...prev, { username, score }]);
    });

    return () => {
      socket.off('quizStart');
      socket.off('startQuiz');
      socket.off('scoreUpdate');
    };
  }, [groupId]);

  const handleFetchQuiz = async () => {
    try {
      const data = await fetchQuiz();
      if (data && data.questions) {
        setQuizQuestions(data.questions);
        setAnswersState({});
        setShowQuiz(true);
        setQuizSubmitted(false);
        setGroupScores([]);
      }
    } catch (err) {
      console.error("Error fetching quiz:", err);
    }
  };

  const handleSubmitQuiz = () => {
    let total = 0;
    quizQuestions.forEach((q, idx) => {
      const selected = answersState[idx]?.selected;
      if (selected === q.correct_answer) total += 1;
    });
    setScore(total);
    setQuizSubmitted(true);

    socket.emit('submitScore', {
      groupId,
      username: currentUsername,
      score: total
    });
  };

  const handleAnswer = (questionIndex, answer, correctAnswer) => {
    if (answersState[questionIndex]) return;
    setAnswersState(prev => ({
      ...prev,
      [questionIndex]: {
        selected: answer,
        correct: answer === correctAnswer
      }
    }));
  };

  const handleJoin = () => {
    if (!token) return alert("Login required");
    joinStudyGroup(groupId, token).then(fetchGroup).catch(console.error);
  };

  const handleLeave = () => {
    if (!token) return alert("Login required");
    leaveStudyGroup(groupId, token).then(fetchGroup).catch(console.error);
  };

  const handleDelete = async () => {
    if (!token) return alert("Login required");
    if (window.confirm("Are you sure?")) {
      try {
        // Call cancel reservation API first if reservation_id exists
        const reservationId = group?.location?.match(/Reservation ID: ([^)]+)/)?.[1];
        if (reservationId && currentUser?.email) {
          await axios.delete(`https://yd0w0dunk0.execute-api.us-east-1.amazonaws.com/dev/api/cancel-group-reservation/`, {
            data: {
              reservation_id: reservationId,
              user: currentUser.email
            }
          });
          console.log("Reservation cancelled successfully.");
        }
      } catch (err) {
        console.error("Error cancelling reservation:", err);
      }

      deleteStudyGroup(groupId, token)
        .then(() => navigate("/"))
        .catch(console.error);
    }
  };

  const handleAdminQuizStart = () => {
    console.log("Admin clicked Start Quiz for All. Group ID:", groupId);
    socket.emit('startQuizByAdmin', { groupId });
  };

  if (!group) return <div className="container">Loading...</div>;

  const isMember = token && currentUsername && group.members.includes(currentUsername);
  const isCreator = token && currentUserId && String(group.creator) === String(currentUserId);

  return (
    <div className="container">
      <h1>{group.subject} Study Group</h1>
      <p><strong>Meeting Time:</strong> {new Date(group.meeting_time).toLocaleString()}</p>
      {group.session_type === 'offline' && <p><strong>Location:</strong> {group.location}</p>}
      {group.session_type === 'online' && (
        <p>
          <strong>Link:</strong>{' '}
          <a href={group.online_meeting_link} target="_blank" rel="noopener noreferrer">
            {group.online_meeting_link}
          </a>
        </p>
      )}

      <div>
        {!isMember && token && <button onClick={handleJoin}>Join Group</button>}
        {isMember && token && <button onClick={handleLeave}>Leave Group</button>}
      </div>

      {isCreator && (
        <div style={{ marginTop: '20px' }}>
          <button onClick={handleDelete} style={{ backgroundColor: 'red', color: 'white' }}>Delete Group</button>
          <button onClick={handleAdminQuizStart} style={{ marginLeft: '10px' }}>Start Quiz for All</button>
        </div>
      )}

      {isMember && !isCreator && (
        <div style={{ marginTop: '20px' }}>
          <button onClick={handleFetchQuiz}>Start Quiz for Me</button>
        </div>
      )}

      {showQuiz && (
        <div className="quiz-box">
          <h3>Quiz Time!</h3>
          {quizQuestions.map((q, idx) => {
            const allAnswers = [...q.incorrect_answers, q.correct_answer].sort();
            const selected = answersState[idx]?.selected;
            const correct = q.correct_answer;

            return (
              <div key={idx} style={{ marginBottom: '20px' }}>
                <p dangerouslySetInnerHTML={{ __html: `${idx + 1}. ${q.question}` }} />
                {allAnswers.map((ans, i) => {
                  let btnStyle = {};
                  if (selected) {
                    if (ans === correct && ans === selected) btnStyle = { backgroundColor: 'green', color: 'white' };
                    else if (ans === selected && ans !== correct) btnStyle = { backgroundColor: 'red', color: 'white' };
                    else if (ans === correct) btnStyle = { backgroundColor: 'green', opacity: 0.7 };
                    else btnStyle = { opacity: 0.5 };
                  }

                  return (
                    <button
                      key={i}
                      disabled={!!selected}
                      onClick={() => handleAnswer(idx, ans, correct)}
                      style={{ margin: '5px', ...btnStyle }}
                      dangerouslySetInnerHTML={{ __html: ans }}
                    />
                  );
                })}

                {selected && (
                  <p>{answersState[idx].correct ? '✅ Correct!' : `❌ Wrong. Correct: ${correct}`}</p>
                )}
              </div>
            );
          })}

          {!quizSubmitted && <button onClick={handleSubmitQuiz}>Submit Quiz</button>}

          {quizSubmitted && (
            <div>
              <h4>You scored {score} / {quizQuestions.length}</h4>
              <h5>Group Scores:</h5>
              <ul>
                {groupScores.map((entry, i) => (
                  <li key={i}>{entry.username}: {entry.score}</li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default StudyGroupDetails;
